// client/src/components/Login.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import AppInstall from './AppInstall';
import './Auth.css';
import { isRegistrationOpen } from '../config/environment';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [retryAfter, setRetryAfter] = useState(0);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  // API URL Helper (Uses localhost in dev, production URL in prod)
  const API_URL = process.env.REACT_APP_API_URL || `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:5001`;

  // Countdown for retry
  useEffect(() => {
    if (retryAfter > 0) {
      const timer = setTimeout(() => {
        setRetryAfter(retryAfter - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [retryAfter]);

  // --- UPDATED SUBMIT LOGIC ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      
      // Perform Login
      const result = await login(email, password);
      
      if (result.success) {
        // Mandatory password change on first admin login
        if (result.user && result.user.mustChangePassword) {
          console.log('Mandatory password change required');
          navigate('/change-password', { replace: true });
          return;
        }
        // Determine Role
        // Note: Check both direct 'role' property or nested inside 'user' object
        const role = result.role || (result.user && result.user.role);
        const isAdmin = result.isAdmin || (result.user && result.user.is_admin);

        console.log('Login Success. Role:', role);

        // --- ROUTING LOGIC ---
        if (role === 'super_admin') {
          console.log('Redirecting to Super Admin Portal');
          navigate('/super-admin', { replace: true });
        } 
        else if (role === 'admin' || isAdmin) {
          console.log('Redirecting to Admin Distributor Panel');
          navigate('/admin', { replace: true }); // Updated to match your route path
        } 
        else {
          console.log('Redirecting to User Lobby');
          navigate('/lobby', { replace: true });
        }
      } else {
        setError(result.message || 'Login failed. Please check credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Cannot connect to server. Please check if the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    try {
      setResetMessage('');
      setResetLoading(true);
      
      // Updated to use dynamic API URL instead of hardcoded domain
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });
      
      // Check for rate limiting
      if (response.status === 429) {
        setResetMessage('Too many reset attempts. Please wait a few minutes and try again.');
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        setResetMessage('If an account exists with this email, a password reset link has been sent. Please check your email.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmail('');
        }, 5000);
      } else {
        setResetMessage(data.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setResetMessage('Network error. Is the server running?');
    } finally {
      setResetLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="auth-page">
        <div className="auth-container login">
          <div className="auth-header">
            <div className="logo">
              <span className="logo-icon">🎴</span>
              <h1>Reset Password</h1>
            </div>
            <p>Enter your email to reset your password</p>
          </div>

          <div className="auth-form-container">
            {resetMessage && (
              <div className={`message ${resetMessage.includes('successful') || resetMessage.includes('reset') ? 'success-message' : 'error-message'}`}>
                {resetMessage}
              </div>
            )}
            
            <form onSubmit={handleForgotPassword} className="auth-form">
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="Enter your registered email"
                  required
                />
              </div>

              <button 
                disabled={resetLoading} 
                type="submit"
                className="auth-button"
              >
                {resetLoading ? (
                  <span className="button-loading">
                    <span className="spinner"></span>
                    Resetting Password...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </div>

          <div className="auth-footer">
            <p>Remember your password?</p>
            <button 
              onClick={() => setShowForgotPassword(false)} 
              className="auth-link-button"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal Login View
  return (
    <div className="auth-page">
      <div className="auth-container login">
        <div className="auth-header">
          <div className="logo">
            <img src="/jv-games-logo.svg" alt="JV Games" style={{ height: '76px', width: 'auto' }} />
          </div>
          <p>Welcome back! Please sign in to your account</p>
        </div>

        <div className="auth-form-container">
          {error && (
            <div className="error-message">
              {error}
              {retryAfter > 0 && (
                <div className="retry-countdown">
                  Retry available in: {retryAfter}s
                </div>
              )}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={retryAfter > 0}
              />
            </div>
            
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={retryAfter > 0}
              />
            </div>

            <div className="forgot-password-link">
              <button 
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="forgot-password-button"
                disabled={retryAfter > 0}
              >
                Forgot Password?
              </button>
            </div>

            <button 
              disabled={loading || retryAfter > 0} 
              type="submit"
              className="auth-button"
            >
              {loading ? (
                <span className="button-loading">
                  <span className="spinner"></span>
                  Signing in...
                </span>
              ) : retryAfter > 0 ? (
                `Try again in ${retryAfter}s`
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        <div className="auth-footer">
          {isRegistrationOpen() && (
            <>
              <p>Don't have an account?</p>
              <Link to="/register" className="auth-link">
                Sign up here
              </Link>
            </>
          )}
        </div>

        <AppInstall />
      </div>
    </div>
  );
};

export default Login;